import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Toast from 'react-bootstrap/Toast';

import RangeSlider from 'react-bootstrap-range-slider';
import SongPlayModal from './SongPlayModal';

import { useContext } from 'react';
import { GlobalStateContext } from './GlobalState';
import PlaylistSaveModal from './PlaylistSaveModal';
import parse from 'html-react-parser';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useNavigate } from 'react-router-dom';

const SqueezeMain = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [dataBeforeFilter, setDataBeforeFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const svgRef = useRef(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [orderingMode, setOrderingMode] = useState('funtorun'); // 'funtorun' or 'tempo'
  const [minFuntorun, setMinFuntorun] = useState(0); // State for minimum funtorun
  const [maxFuntorun, setMaxFuntorun] = useState(200); // State for maximum funtorun
  const [minTempo, setMinTempo] = useState(0); // State for minimum tempo
  const [maxTempo, setMaxTempo] = useState(200); // State for maximum tempo

  const [targetMinutes, setTargetMinutes] = useState(200);
  const [dataBeforeShrink, setDataBeforeShrink] = useState([]);
  const [originalDuration, setOriginalDuration] = useState(0);


  const { addListToGlobalList, setGlobalList } = useContext(GlobalStateContext);
  const [showToast, setShowToast] = useState(false);
  const { globalList } = useContext(GlobalStateContext);
  const { clearGlobalList } = useContext(GlobalStateContext);


  // Function to filter data based on the funtorun range
  const filterDataByFuntorun = (min, max) => {
    return dataBeforeFilter.filter(d => d.funtorun >= min && d.funtorun <= max);
  };

  // Function to filter data based on the tempo range
  const filterDataByTempo = (min, max) => {
    return dataBeforeFilter.filter(d => d.tempo >= min && d.tempo <= max);
  };
  const handleDelete = (index) => {
    const newSongs = [...data];
    newSongs.splice(index, 1);
    setData(newSongs);
    setGlobalList(newSongs);
    setDataBeforeShrink(newSongs);
  };

  // Add functions to handle funtorun range changes
  const [tempFilteredData, setTempFilteredData] = useState(null);

  const handleMinFuntorunChange = (e) => {
    const newMin = parseInt(e.target.value);
    setMinFuntorun(newMin);
    const filteredData = filterDataByFuntorun(newMin, maxFuntorun);
    setTempFilteredData(filteredData);
    setData(filteredData);
    renderChart(filteredData);
  };

  const handleConfirmFilter = () => {
    if (tempFilteredData) {
      setGlobalList(tempFilteredData);
      setTempFilteredData(null);
    }
  };

  const handleMaxFuntorunChange = (e) => {
    const newMax = parseInt(e.target.value);
    setMaxFuntorun(newMax);
    const filteredData = filterDataByFuntorun(minFuntorun, newMax);
    setTempFilteredData(filteredData);
    setData(filteredData);
    renderChart(filteredData);
  };

  const handleMinTempoChange = (e) => {
    const newMin = parseInt(e.target.value);
    setMinTempo(newMin);
    const filteredData = filterDataByTempo(newMin, maxTempo);
    setTempFilteredData(filteredData);
    setData(filteredData);
    renderChart(filteredData);
  };

  const handleMaxTempoChange = (e) => {
    const newMax = parseInt(e.target.value);
    setMaxTempo(newMax);
    const filteredData = filterDataByTempo(minTempo, newMax);
    setTempFilteredData(filteredData);
    setData(filteredData);
    renderChart(filteredData);
  };

  // ... (rest of the code remains unchanged)
  const handleSortAsc = () => {
    setSortOrder('asc');
    const sortedData = [...data].sort((a, b) => {
      return a[orderingMode] - b[orderingMode];
    });
    setData(sortedData);
    setDataBeforeShrink(sortedData);
    setGlobalList(sortedData);
    renderChart(sortedData);

  };

  const handleSortDesc = () => {
    setSortOrder('desc');
    const sortedData = [...data].sort((a, b) => {
      return b[orderingMode] - a[orderingMode];
    });
    setData(sortedData);
    setDataBeforeShrink(sortedData);
    setGlobalList(sortedData);
    renderChart(sortedData);
  };

  const handleSortArc = () => {
    // First sort all data by orderingMode (funtorun or tempo)
    const sortedData = [...data].sort((a, b) => a[orderingMode] - b[orderingMode]);
    const result = new Array(sortedData.length);

    // Find the middle index
    const midIndex = Math.floor(sortedData.length / 2);

    // Place highest values in the middle, then distribute outwards
    let left = midIndex - 1;  // moving left from middle
    let right = midIndex + 1; // moving right from middle
    let sortedIndex = sortedData.length - 1; // start with highest values

    // Place the highest value in the middle
    result[midIndex] = sortedData[sortedIndex--];

    // Distribute remaining values alternating between left and right sides
    while (left >= 0 && right < sortedData.length) {
      // Place next highest value on the left
      result[left] = sortedData[sortedIndex--];
      left--;

      // Place next highest value on the right
      if (sortedIndex >= 0) {
        result[right] = sortedData[sortedIndex--];
        right++;
      }
    }

    // Fill any remaining positions
    while (left >= 0) {
      result[left] = sortedData[sortedIndex--];
      left--;
    }
    while (right < sortedData.length) {
      result[right] = sortedData[sortedIndex--];
      right++;
    }

    setData(result);
    setDataBeforeShrink(result);
    setGlobalList(result);
    renderChart(result);
  };
  const handleShuffle = () => {
    // Fisher-Yates (Durstenfeld) shuffle
    const shuffledData = [...data];
    for (let i = shuffledData.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledData[i], shuffledData[j]] = [shuffledData[j], shuffledData[i]];
    }
    setData(shuffledData);
    setDataBeforeShrink(shuffledData);
    setGlobalList(shuffledData);
    renderChart(shuffledData);
  };

  const shrinkPlaylist = (data, targetMinutes) => {
    const currentDurationInSeconds = data.reduce((acc, song) => acc + song.duration_s, 0);
    const currentDurationInMinutes = currentDurationInSeconds / 60;

    if (targetMinutes >= currentDurationInMinutes) return data; // No shrinking needed

    // Calculate how many songs we need to keep based on average song duration
    const avgSongDuration = currentDurationInSeconds / data.length;
    const targetNumSongs = Math.floor((targetMinutes * 60) / avgSongDuration);

    // Calculate how many items we need to remove
    const itemsToRemove = data.length - targetNumSongs;

    // Calculate the stride (how many items to skip between selections)
    const stride = data.length / itemsToRemove;

    // Create an array of indices to remove
    const indicesToRemove = new Set();
    for (let i = 0; i < itemsToRemove; i++) {
      const index = Math.round(i * stride);
      if (index < data.length) {
        indicesToRemove.add(index);
      }
    }

    // Filter out the marked indices
    const result = data.filter((_, index) => !indicesToRemove.has(index));

    return result;
  };

  const [tempShrunkData, setTempShrunkData] = useState(null);

  const handleShrinkPlaylist = (e) => {
    const targetMinutes = parseInt(e.target.value);
    const shrunkData = shrinkPlaylist(dataBeforeShrink, targetMinutes);
    setTargetMinutes(targetMinutes);
    setTempShrunkData(shrunkData);
    setData(shrunkData);
    renderChart(shrunkData);
  };

  const handleConfirmShrink = () => {
    if (tempShrunkData) {
      setGlobalList(tempShrunkData);
      setTempShrunkData(null);
    }
  };

  const handleConfirmRemovals = () => {
    if (tempShrunkData) {
      setGlobalList(tempShrunkData);
      setTempShrunkData(null);
    }
    if (tempFilteredData) {
      setGlobalList(tempFilteredData);
      setTempFilteredData(null);
    }
  };


  const handleLocalShuffle = () => {
    const shuffledData = [...data];
    // Process the array in groups of 5
    for (let i = 0; i < shuffledData.length; i += 5) {
      // Get the current group (up to 5 elements)
      const endIndex = Math.min(i + 5, shuffledData.length);
      const group = shuffledData.slice(i, endIndex);

      // Shuffle just this group using Fisher-Yates
      for (let j = group.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1));
        [group[j], group[k]] = [group[k], group[j]];
      }

      // Put the shuffled group back into the array
      for (let j = 0; j < group.length; j++) {
        shuffledData[i + j] = group[j];
      }
    }

    setData(shuffledData);
    setDataBeforeShrink(shuffledData);
    setGlobalList(shuffledData);
    renderChart(shuffledData);
  };

  const handleSortFartlek1 = () => {
    const sortedData = [...data].sort((a, b) => a[orderingMode] - b[orderingMode]);

    let mixed = [];
    let i2 = (sortedData.length - 1);
    for (let i = 0; i < (sortedData.length / 2); i++) {
      if (i < ((sortedData.length / 2) << 0)) {
        mixed.push(sortedData[i]);
        mixed.push(sortedData[i2]);
        i2 = i2 - 1;
      }
      else {
        i = i + 1;
        mixed.push(sortedData[i]);
      }
    }

    setData(mixed);
    setDataBeforeShrink(mixed);
    setGlobalList(mixed);
    renderChart(mixed);
  };

  const handleSortFartlek2 = () => {
    const sortedData = [...data].sort((a, b) => a[orderingMode] - b[orderingMode]);

    let mixed = [];
    let i2 = (sortedData.length - 1);
    for (let i = 0; i < (sortedData.length / 2); i++) {
      if (i < ((sortedData.length / 2) << 0)) {
        mixed.push(sortedData[i]);
        mixed.push(sortedData[i2]);
        i2 = i2 - 1;
      }
      else {
        i = i + 1;
        mixed.push(sortedData[i]);
      }
    }

    // Invert the order of the mixed array
    mixed = mixed.reverse();

    setData(mixed);
    setDataBeforeShrink(mixed);
    setGlobalList(mixed);
    renderChart(mixed);
  };

  const renderChart = (data) => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous chart elements
    const margin = { top: 20, right: 20, bottom: 30, left: 50 };
    const width = svg.attr('width') - margin.left - margin.right;
    const height = svg.attr('height') - margin.top - margin.bottom;

    // X-axis (unchanged)
    const x = d3.scaleBand()
      .range([0, width])
      .domain(data.map(d => d.name))
      .padding(0.1);

    // Y-axis: Dynamic domain based on mode
    const yMax = orderingMode === 'funtorun'
      ? Math.max(100, d3.max(data, d => d.funtorun) || 0)
      : Math.max(200, d3.max(data, d => d.tempo) || 0);

    const y = d3.scaleLinear()
      .range([height, 0])
      .domain([0, yMax]);

    // Add Y-axis with label
    const yAxisLabel = orderingMode === 'funtorun' ? 'Run-Boost' : 'BPM';

    svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
      .call(d3.axisLeft(y));

    // Add Y-axis label
    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 10)
      .attr('x', -(height / 2) - margin.top)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('fill', 'white')
      .style('font-size', '12px')
      .text(yAxisLabel);

    // Dynamic chart color
    const chartColor = orderingMode === 'funtorun' ? 'steelblue' : 'red';

    // Lollipop circles: Use dynamic property
    svg.selectAll('.lollipop')
      .data(data)
      .enter()
      .append('circle')
      .attr('class', 'lollipop')
      .attr('cx', d => x(d.name) + x.bandwidth() / 2 + margin.left)
      .attr('cy', d => y(d[orderingMode]) + margin.top)
      .attr('r', 5)
      .attr('fill', chartColor);
  };

  useEffect(() => {
    //console.log('@squeezemain:useeffect1 running');
    setIsLoading(true);
    setData(globalList);
    setDataBeforeFilter(globalList);
    setDataBeforeShrink(globalList);
    const duration = globalList.reduce((acc, song) => acc + song.duration_s, 0) / 60;
    setOriginalDuration(Math.round(duration));
    setTargetMinutes(Math.round(duration));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Do something when globalStateValue changes
    //console.log('@squeezemain:useeffect2 running');
    //console.log('Squeeze-main- Global state value changed:', globalList);
    setDataBeforeFilter(globalList);
    setData(globalList);
  }, [globalList]);


  useEffect(() => {
    if (!isLoading) {
      renderChart(data);
    }
  }, [data, isLoading, orderingMode]);

  const totalDurationInSeconds = data.reduce((acc, song) => acc + song.duration_s, 0);
  const totalDurationInMinutes = Math.round(totalDurationInSeconds / 60);

  const handleClearPlaylist = () => {
    clearGlobalList();
  };

  return (
    <div
      className="container"
      style={{ backgroundColor: "black", color: "white" }}
      align="center"
    >
      {isLoading ? (
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      ) : (
        <>
          <h1 className="cover-heading">
            <strong className="step-number">2</strong> Filter and sort the
            Playlist
          </h1>
          <p style={{ textAlign: 'left' }}>
            You can select songs between a minimum and maximum <b>run-boost</b> or <b>BPM</b> to match your running pace and
            order the songs to support your workout picking a <b>run shape</b>
            (e.g. ascending, descending, fartlek).
          </p>


          <div style={{ maxWidth: "400px" }} align="center">
            <p>
              Total Duration:
              {totalDurationInMinutes}
              {" minutes"}
            </p>
            <svg ref={svgRef} width={250} height={200} />

            <div className="d-flex justify-content-center mb-3">
              <ButtonGroup>
                <Button
                  variant={orderingMode === 'funtorun' ? 'primary' : 'outline-primary'}
                  onClick={() => setOrderingMode('funtorun')}
                >
                  Run-Boost
                </Button>
                <Button
                  variant={orderingMode === 'tempo' ? 'primary' : 'outline-primary'}
                  onClick={() => setOrderingMode('tempo')}
                >
                  BPM
                </Button>
              </ButtonGroup>
            </div>

            {/* Conditional filter ordering based on mode */}
            {orderingMode === 'funtorun' ? (
              <div className="row">
                <div className="col-1">
                  <label>Run Boost </label>
                </div>
                <div className="col-11">
                  <div className="row">
                    <div className="col-5">
                      <label>Min: {minFuntorun}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={minFuntorun}
                        min={0}
                        max={100}
                        onChange={handleMinFuntorunChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-5">
                      <label>Max: {maxFuntorun}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={maxFuntorun}
                        min={0}
                        max={100}
                        onChange={handleMaxFuntorunChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                </div>
                <br />
              </div>
            ) : (
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-2">
                  <label>Tempo (BPM) </label>
                </div>
                <div className="col-10">
                  <div className="row">
                    <div className="col-5">
                      <label>Min: {minTempo}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={minTempo}
                        min={0}
                        max={200}
                        onChange={handleMinTempoChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-5">
                      <label>Max: {maxTempo}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={maxTempo}
                        min={0}
                        max={200}
                        onChange={handleMaxTempoChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <br />
            <div className="d-flex align-items-start">
              <div style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', marginRight: '10px', height: '120px' }}>
                <label>Run Shape</label>
              </div>
              <div className="flex-grow-1">
                <div className="row g-2">
                  <div className="col-6">
                    <Button variant="primary" onClick={handleSortAsc} style={{ width: '100%', height: '50px', marginBottom: '10px' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-graph-up-arrow"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M0 0h1v15h15v1H0V0Zm10 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V4.9l-3.613 4.417a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61L13.445 4H10.5a.5.5 0 0 1-.5-.5Z"
                        />
                      </svg> Up
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button variant="primary" onClick={handleSortDesc} style={{ width: '100%', height: '50px', marginBottom: '10px' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-graph-down-arrow"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M0 0h1v15h15v1H0V0Zm10 11.5a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 0-1 0v2.6l-3.613-4.417a.5.5 0 0 0-.74-.037L7.06 8.233 3.404 3.206a.5.5 0 0 0-.808.588l4 5.5a.5.5 0 0 0 .758.06l2.609-2.61L13.445 11H10.5a.5.5 0 0 0-.5.5Z"
                        />
                      </svg> Down
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button variant="primary" onClick={handleSortArc} style={{ width: '100%', height: '50px' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        class="bi bi-rainbow"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 4.5a7 7 0 0 0-7 7 .5.5 0 0 1-1 0 8 8 0 1 1 16 0 .5.5 0 0 1-1 0 7 7 0 0 0-7-7zm0 2a5 5 0 0 0-5 5 .5.5 0 0 1-1 0 6 6 0 1 1 12 0 .5.5 0 0 1-1 0 5 5 0 0 0-5-5zm0 2a3 3 0 0 0-3 3 .5.5 0 0 1-1 0 4 4 0 1 1 8 0 .5.5 0 0 1-1 0 3 3 0 0 0-3-3zm0 2a1 1 0 0 0-1 1 .5.5 0 0 1-1 0 2 2 0 1 1 4 0 .5.5 0 0 1-1 0 1 1 0 0 0-1-1z" />
                      </svg> Arc
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button variant="primary" onClick={handleSortFartlek1} style={{ width: '100%', height: '50px' }}>
                      <i className="bi bi-arrow-down-up"></i> Fartlek DESC
                    </Button>
                    <Button variant="primary" onClick={handleSortFartlek2} style={{ width: '100%', height: '50px' }}>
                      <i className="bi bi-arrow-down-up"></i> Fartlek ASC
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <p style={{ textAlign: 'left' }}>
              You can also shuffle randomly the full playlist or just partially while keeping roughly the original run shape.
            </p>

            <div className="d-flex align-items-start">
              <div style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', marginRight: '10px', height: '50px' }}>
                <label>Shuffle</label>
              </div>
              <div className="flex-grow-1">
                <div className="row g-2">
                  <div className="col-6">
                    <Button variant="primary" onClick={handleShuffle} style={{ width: '100%', height: '50px' }}>
                      <i className="bi bi-shuffle" title="Random order"></i> Global
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button variant="primary" onClick={handleLocalShuffle} style={{ width: '100%', height: '50px' }}>
                      <i className="bi bi-shuffle" title="Shuffle in groups of 5"></i> Partial
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <br />
            <p style={{ textAlign: 'left' }}>
              Finally set the lenght of the playlist to match the length of your run while keeping the run shape .
            </p>


            <div className="row" style={{ marginBottom: '20px' }}>
              <div className="col-2">
                <label>Target Duration </label>
              </div>
              <div className="col-2">
                <label>{targetMinutes}</label>
              </div>
              <div className="col-7">
                <RangeSlider
                  value={targetMinutes}
                  min={0}
                  max={originalDuration}
                  onChange={handleShrinkPlaylist}
                  tooltip={"off"}
                  variant={"primary"}
                />
              </div>
              <div className="col-1">
              </div>
            </div>

            {/* Second filter (opposite of the first one) */}
            {orderingMode === 'funtorun' ? (
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-2">
                  <label>Tempo (BPM) </label>
                </div>
                <div className="col-10">
                  <div className="row">
                    <div className="col-5">
                      <label>Min: {minTempo}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={minTempo}
                        min={0}
                        max={200}
                        onChange={handleMinTempoChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-5">
                      <label>Max: {maxTempo}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={maxTempo}
                        min={0}
                        max={200}
                        onChange={handleMaxTempoChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-1">
                  <label>Run Boost </label>
                </div>
                <div className="col-11">
                  <div className="row">
                    <div className="col-5">
                      <label>Min: {minFuntorun}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={minFuntorun}
                        min={0}
                        max={100}
                        onChange={handleMinFuntorunChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                  <div className="row">
                    <div className="col-5">
                      <label>Max: {maxFuntorun}</label>
                    </div>
                    <div className="col-6">
                      <RangeSlider
                        value={maxFuntorun}
                        min={0}
                        max={100}
                        onChange={handleMaxFuntorunChange}
                        tooltip={"off"}
                        variant={"primary"}
                        style={{ width: '120px' }}
                      />
                    </div>
                  </div>
                  <br />
                </div>
                <br />
              </div>
            )}

            <div className="row">
              <div className="col-2"></div>
              <div className="col-8">
                {(tempFilteredData || tempShrunkData) && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleConfirmRemovals}
                    title="Confirm Shrinking"
                  > Confirm New Playlist Shrink
                    <i className="bi bi-check-circle"></i>
                  </Button>
                )}
              </div>
              <div className="col-2">
              </div>
              <br />
            </div>

          </div>



          <br />
          <br />
          <br />
          <Table responsive bordered variant="dark">
            <thead>
              <tr>
                <th style={{ color: "steelblue" }}>Name</th>
                <th style={{ color: "steelblue" }}></th>
                <th style={{ color: "steelblue" }}>Preview</th>
                <th style={{ color: "steelblue" }}>Run Boost</th>
                <th style={{ color: "steelblue" }}>BPM</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={i}>
                  <td>
                    {d.artist}-
                    <span style={{ color: "steelblue" }}>{d.name}</span>
                  </td>
                  <td>
                    <i
                      class="bi bi-x-circle-fill"
                      style={{ fontSize: "1.5em" }}
                      onClick={() => handleDelete(i)}
                    ></i>
                  </td>
                  <td>
                    <SongPlayModal
                      classvalue="bi bi-music-note-beamed"
                      linkText=""
                      songId={d.id}
                      modalTitle={d.artist + "-" + d.name}
                      width="250"
                      height="100"
                    />
                  </td>
                  <td>{Math.round(d.funtorun)}<br />
                    <ProgressBar now={d.energy * 100} label="E" />
                    <ProgressBar now={d.danceability * 100} label="R" />

                    {/*E:{Math.round(d.energy*10)} -R:{Math.round(d.danceability*10)} */}
                  </td>
                  <td>{Math.round(d.tempo)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <br />
          {/*}
         <PlaylistSaveModal
           list={data}
           linkText="Save your playlist"
           modalTitle="Saving playlist"
         />
         */}
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="warning" onClick={handleClearPlaylist}>
              Clear Playlist
            </Button>
            <Button variant="info" onClick={() => {
              navigate('/chat', { state: { systemContext: data } });
            }}>
              <i className="bi bi-chat-dots"></i> Open in Chat
            </Button>
          </div>
        </>
      )}

      <div style={{ padding: "40px" }}>
        Songs metadata provided by Spotify.{" "}
        <a href="https://open.spotify.com/" target="_blank" rel="noreferrer">
          {" "}
          <img
            src={require("./s2rimages/listenonspotify.png")}
            alt="Spotify site"
            width="160px"
          />
        </a>
      </div>
    </div>
  );
};




export default SqueezeMain;
